import { Alert } from "./alert";
import { Knopf } from "./knopf";

/**
 * Handlungskarte „Deine nächste Handlung“ (Musterkatalog M-011 AD4/AD7, Design-Guide `handlungskarte.md`): eine
 * Primärhandlung, höchstens drei Nebenaktionen, darüber die Rollenzeile („als Projektleitung“). Der Baustein kennt
 * keine Fachregeln und prüft keine Rechte: WAS angeboten wird, entscheidet die eine Regel-Funktion der Fachlogik aus
 * denselben Prüfungen wie die Actions (r109/r110); der Aufrufer reicht nur durch, was die Person darf. Lesende bekommen
 * statt Knöpfen den Hinweis „Nur ansehen“ (`hinweis`). Ein Fehlschlag einer Handlung steht als Hinweissatz IN der Karte
 * (`meldung`, r98/r105), die Karte bleibt offen. Der Slot `formular` nimmt aufklappende Formulare auf (Zurückgeben mit Pflichtgrund).
 * Der frühere Slot `kiHinweis` ist mit A4′ entfallen (Betreiber 14.10.2026): Hornaut steht nur noch in der Karte „Bezüge“ (Zeile
 * „Hornaut · Hinweise“), nie in der Handlungskarte.
 */
export interface HandlungsKnopf {
  id: string;
  label: string;
  /** Aktion in der Seite; fehlt `href`, ist `onClick` Pflicht. */
  onClick?: () => void;
  /** Navigation statt Aktion (z. B. Übergang in eine andere Ansicht): rendert einen Link im Knopf-Stil, kein `onClick`. */
  href?: string;
  deaktiviert?: boolean;
  /** Der Vorgang läuft: Knopf gesperrt, Beschriftung bleibt. */
  laeuft?: boolean;
  /** Lucide-Symbol vor dem Text (nur Primärknopf; Aufrufer setzt `shrink-0 size-4 aria-hidden`). */
  symbol?: React.ReactNode;
}

export function Handlungskarte({
  id,
  rollenZeile,
  primaer,
  neben = [],
  hinweis,
  meldung,
  formular,
  titel = "Deine nächste Handlung",
  kopf,
  nebenAlsLinks = false,
  zusatzZeile,
}: {
  id: string;
  rollenZeile: string;
  primaer: HandlungsKnopf | null;
  neben?: HandlungsKnopf[];
  /** Statt Knöpfen (Lesende): ein Satz, z. B. „Nur ansehen. Du hast Leserechte.“ */
  hinweis?: string;
  /** Satz zu einem gescheiterten oder abgelehnten Vorgang (r105), steht in der Karte. */
  meldung?: string;
  formular?: React.ReactNode;
  titel?: string;
  /**
   * Zustand und Handlung in EINER Karte (W204 B, 15.10.2026): ein `ZustandsKopf` steht oben bündig in der Kartenhülle, die Handlung
   * darunter. Ohne `kopf` (Normalfall, Ruhe) bleibt es bei der Karte wie zuvor.
   */
  kopf?: React.ReactNode;
  /**
   * Nebenaktionen als ruhige Textknöpfe (`Knopf variante="text"`) in einer Zeile statt als Knöpfe. Unabhängig von `kopf`: das Aufgaben-Detail setzt
   * es immer (mit und ohne Zustand-Kopf, W204 B; angeglichen W206 A6 (f), `handlungskarte.md`); andere Verbraucher (Besprechungen, Entscheidungen)
   * behalten ohne die Eigenschaft die Knöpfe.
   */
  nebenAlsLinks?: boolean;
  /**
   * Eine Zeile mit einem zweiten Zustand unter dem Kopf, über „Deine nächste Handlung“ (Handlungszentrale H1, Entscheid O1: „Blockiert durch PRJ-004“, wenn der
   * Zustandsträger nicht die Blockade ist). Der Aufrufer liefert den fertigen Satz (mit Symbol, ohne eigene Farbe); fehlt sie, bleibt die Karte wie zuvor.
   */
  zusatzZeile?: React.ReactNode;
}) {
  const titelId = `${id}-titel`;
  return (
    <>
      {/* Preline: dashboards/admin-dashboard/index.html#browsers-card (Kartenhülle, wie die KI-Karte) */}
      <section aria-labelledby={titelId} className="bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden">
        {kopf}
        <div className="p-4 space-y-3">
        {zusatzZeile}
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 id={titelId} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground-1">
            {titel}
          </h3>
          <span className="text-xs text-muted-foreground-1">{rollenZeile}</span>
        </div>
        {primaer &&
          (primaer.href !== undefined ? (
            <Knopf variante="primaer" groesse="lg" volleBreite="immer" symbol={primaer.symbol} href={primaer.href}>
              {primaer.label}
            </Knopf>
          ) : (
            <Knopf variante="primaer" groesse="lg" volleBreite="immer" symbol={primaer.symbol} onClick={primaer.onClick} disabled={primaer.deaktiviert} laedt={primaer.laeuft}>
              {primaer.label}
            </Knopf>
          ))}
        {neben.length > 0 && (
          <div className={nebenAlsLinks ? "flex flex-wrap gap-x-4 gap-y-1" : "flex flex-wrap gap-2"}>
            {neben.map((n) =>
              n.href !== undefined ? (
                // Abweichung AW-057 (2): Navigation statt Aktion (E-c: „Änderungsvermerk“ führt auf die Bearbeiten-Seite): ein Link im Knopf-Stil, wie beim Primärknopf.
                <Knopf key={n.id} variante={nebenAlsLinks ? "text" : undefined} href={n.href}>
                  {n.label}
                </Knopf>
              ) : (
                <Knopf key={n.id} variante={nebenAlsLinks ? "text" : undefined} onClick={n.onClick} disabled={n.deaktiviert} laedt={n.laeuft}>
                  {n.label}
                </Knopf>
              ),
            )}
          </div>
        )}
        {hinweis && <p className="text-sm text-muted-foreground-1">{hinweis}</p>}
        {meldung && <Alert ton="warnung" id={`${id}-meldung`}>{meldung}</Alert>}
        {formular}
        </div>
      </section>
    </>
  );
}
